import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency, formatDateTime } from "@/lib/utils-format";
import { UploadSimple, CheckCircle, Warning, Question, WarningCircle, Eye, Trash } from "@phosphor-icons/react";

const STATUS_META = {
  matched: { label: "Matched", cls: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle },
  overcharge: { label: "Overcharge", cls: "bg-red-50 text-red-700 border-red-300", icon: Warning },
  undercharge: { label: "Undercharge", cls: "bg-amber-50 text-amber-700 border-amber-300", icon: Warning },
  missing: { label: "No Order", cls: "bg-zinc-100 text-zinc-700 border-zinc-300", icon: Question },
  duplicate: { label: "Duplicate", cls: "bg-red-50 text-red-700 border-red-300", icon: WarningCircle },
  no_expected: { label: "No Quote", cls: "bg-zinc-100 text-zinc-500 border-zinc-300", icon: Question },
};

export default function Reconciliation() {
  const [batches, setBatches] = useState([]);
  const [openUpload, setOpenUpload] = useState(false);
  const [openView, setOpenView] = useState(null);

  const load = async () => setBatches((await api.get("/reconciliation")).data);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this reconciliation batch?")) return;
    try { await api.delete(`/reconciliation/${id}`); load(); toast.success("Deleted"); } catch { toast.error("Failed"); }
  };

  const openBatch = async (batch_id) => {
    const r = await api.get(`/reconciliation/${batch_id}`);
    setOpenView(r.data);
  };

  return (
    <div className="p-8" data-testid="reconciliation-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Finance</p>
          <h1 className="text-4xl font-black tracking-tighter">Courier Reconciliation</h1>
          <p className="text-sm text-zinc-500 mt-2">Upload FedEx / DHL / UPS invoice CSVs. Flags variances, missing shipments, and duplicates.</p>
        </div>
        <Button onClick={() => setOpenUpload(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="upload-reconciliation-button">
          <UploadSimple size={16} weight="bold" className="mr-1" /> Upload Invoice
        </Button>
      </div>

      <div className="border border-zinc-200 bg-white">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2.5">Batch #</th>
              <th className="text-left px-4 py-2.5">Uploaded</th>
              <th className="text-left px-4 py-2.5">Courier</th>
              <th className="text-right px-4 py-2.5">Rows</th>
              <th className="text-right px-4 py-2.5">Matched</th>
              <th className="text-right px-4 py-2.5">Flags</th>
              <th className="text-right px-4 py-2.5">Total Variance</th>
              <th className="text-right px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => {
              const flags = b.variance_count + b.missing_count + b.duplicate_count;
              return (
                <tr key={b.batch_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`batch-row-${b.batch_id}`}>
                  <td className="px-4 py-3 font-mono text-sm font-semibold">{b.batch_number}</td>
                  <td className="px-4 py-3 text-xs">{formatDateTime(b.created_at)}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 text-[11px] uppercase tracking-widest font-semibold bg-zinc-100 border border-zinc-300 rounded">{b.courier}</span></td>
                  <td className="px-4 py-3 text-right font-mono text-sm">{b.total_rows}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-emerald-700">{b.matched_count}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm">
                    <span className={flags > 0 ? "text-red-700 font-semibold" : "text-zinc-400"}>{flags}</span>
                  </td>
                  <td className={`px-4 py-3 text-right font-mono text-sm ${b.total_variance > 0 ? "text-red-700 font-semibold" : b.total_variance < 0 ? "text-amber-700" : ""}`}>
                    {b.total_variance >= 0 ? "+" : ""}{formatCurrency(b.total_variance)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openBatch(b.batch_id)} className="p-1 hover:bg-zinc-200 rounded" title="Review" data-testid={`view-batch-${b.batch_id}`}>
                        <Eye size={14} />
                      </button>
                      <button onClick={() => remove(b.batch_id)} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 rounded" title="Delete" data-testid={`delete-batch-${b.batch_id}`}>
                        <Trash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {batches.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-sm text-zinc-500">No reconciliation batches yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <UploadDialog open={openUpload} onClose={() => setOpenUpload(false)} onDone={(b) => { setOpenUpload(false); load(); setOpenView(b); }} />
      {openView && <BatchDialog batch={openView} onClose={() => setOpenView(null)} onRefresh={async () => setOpenView((await api.get(`/reconciliation/${openView.batch_id}`)).data)} />}
    </div>
  );
}

function UploadDialog({ open, onClose, onDone }) {
  const [courier, setCourier] = useState("FedEx");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Choose a CSV file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("courier", courier);
      fd.append("file", file);
      const r = await api.post("/reconciliation/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded: ${r.data.matched_count} matched, ${r.data.variance_count + r.data.missing_count + r.data.duplicate_count} flags`);
      onDone(r.data);
    } catch (error) { toast.error(error.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="upload-batch-dialog">
        <DialogHeader><DialogTitle>Upload Courier Invoice</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Courier</Label>
            <Select value={courier} onValueChange={setCourier}>
              <SelectTrigger className="border-zinc-300 rounded-md mt-1" data-testid="upload-courier-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FedEx">FedEx</SelectItem>
                <SelectItem value="DHL">DHL</SelectItem>
                <SelectItem value="UPS">UPS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Invoice CSV</Label>
            <Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="border-zinc-300 rounded-md mt-1" data-testid="upload-batch-file" />
            <p className="text-[10px] text-zinc-500 mt-2">Accepted columns: Tracking Number, Reference, Amount, Weight, Service, Invoice Date. Any common variant will map.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="border-zinc-300 rounded-md">Cancel</Button>
            <Button onClick={submit} disabled={busy || !file} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="submit-upload-button">
              <UploadSimple size={14} weight="bold" className="mr-1" /> {busy ? "Uploading…" : "Upload & Match"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BatchDialog({ batch, onClose, onRefresh }) {
  const [filter, setFilter] = useState("all");
  const [resolveLine, setResolveLine] = useState(null);

  const filtered = batch.lines.filter((l) => {
    if (filter === "all") return true;
    if (filter === "flagged") return l.status !== "matched";
    return l.status === filter;
  });

  const resolve = async (lineId, resolution, note) => {
    try {
      await api.post(`/reconciliation/${batch.batch_id}/lines/${lineId}/resolve`, { resolution, note });
      toast.success("Line resolved");
      onRefresh();
      setResolveLine(null);
    } catch { toast.error("Failed"); }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-6xl bg-white max-h-[90vh] overflow-y-auto" data-testid="batch-dialog">
          <DialogHeader><DialogTitle>{batch.batch_number} · {batch.courier}</DialogTitle></DialogHeader>
          <div>
            <div className="grid grid-cols-5 gap-3 mb-4">
              <Stat label="Rows" value={batch.total_rows} />
              <Stat label="Matched" value={batch.matched_count} color="text-emerald-700" />
              <Stat label="Variances" value={batch.variance_count} color={batch.variance_count > 0 ? "text-amber-700" : ""} />
              <Stat label="Missing" value={batch.missing_count} color={batch.missing_count > 0 ? "text-zinc-700" : ""} />
              <Stat label="Duplicates" value={batch.duplicate_count} color={batch.duplicate_count > 0 ? "text-red-700" : ""} />
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <Stat label="Total Invoiced" value={formatCurrency(batch.total_invoiced)} />
              <Stat label="Total Expected" value={formatCurrency(batch.total_expected)} />
              <Stat label="Total Variance" value={`${batch.total_variance >= 0 ? "+" : ""}${formatCurrency(batch.total_variance)}`} color={batch.total_variance > 0 ? "text-red-700 font-black" : batch.total_variance < 0 ? "text-amber-700" : ""} big />
            </div>

            <div className="flex items-center gap-2 mb-2">
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Filter</Label>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[200px] h-8 border-zinc-300 rounded-md" data-testid="batch-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All rows</SelectItem>
                  <SelectItem value="flagged">Flagged only</SelectItem>
                  <SelectItem value="overcharge">Overcharge</SelectItem>
                  <SelectItem value="undercharge">Undercharge</SelectItem>
                  <SelectItem value="missing">Missing</SelectItem>
                  <SelectItem value="duplicate">Duplicate</SelectItem>
                  <SelectItem value="matched">Matched</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 ml-2">{filtered.length} of {batch.lines.length}</p>
            </div>

            <div className="border border-zinc-200 max-h-[400px] overflow-y-auto" data-testid="batch-lines-table">
              <table className="w-full data-table">
                <thead className="sticky top-0 bg-zinc-50">
                  <tr>
                    <th className="text-left px-3 py-2">Row</th>
                    <th className="text-left px-3 py-2">Tracking / Ref</th>
                    <th className="text-left px-3 py-2">Order</th>
                    <th className="text-right px-3 py-2">Invoiced</th>
                    <th className="text-right px-3 py-2">Expected</th>
                    <th className="text-right px-3 py-2">Variance</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => {
                    const meta = STATUS_META[l.status] || STATUS_META.matched;
                    const Icon = meta.icon;
                    return (
                      <tr key={l.line_id} className={`border-b border-zinc-100 ${l.resolved ? "opacity-50" : ""}`} data-testid={`batch-line-${l.line_id}`}>
                        <td className="px-3 py-2 text-xs font-mono">{l.row_number}</td>
                        <td className="px-3 py-2 text-xs font-mono">{l.tracking_number || l.reference_number || "—"}</td>
                        <td className="px-3 py-2 text-xs font-mono">{l.order_number || <span className="text-zinc-400">—</span>}</td>
                        <td className="px-3 py-2 text-right text-xs font-mono">{formatCurrency(l.invoiced_amount)}</td>
                        <td className="px-3 py-2 text-right text-xs font-mono">{l.expected_amount ? formatCurrency(l.expected_amount) : "—"}</td>
                        <td className={`px-3 py-2 text-right text-xs font-mono font-semibold ${l.variance > 0 ? "text-red-700" : l.variance < 0 ? "text-amber-700" : ""}`}>
                          {l.variance >= 0 ? "+" : ""}{formatCurrency(l.variance)}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded ${meta.cls}`}>
                            <Icon size={11} weight="bold" /> {meta.label}
                            {l.resolved && <span className="ml-1 text-[9px] uppercase">· {l.resolution}</span>}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!l.resolved && l.status !== "matched" && (
                            <button onClick={() => setResolveLine(l)} className="text-[10px] uppercase tracking-widest text-zinc-600 hover:text-zinc-900 font-semibold" data-testid={`resolve-${l.line_id}`}>
                              Resolve
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {resolveLine && (
        <ResolveDialog line={resolveLine} onClose={() => setResolveLine(null)} onResolve={resolve} />
      )}
    </>
  );
}

function ResolveDialog({ line, onClose, onResolve }) {
  const [resolution, setResolution] = useState("accepted");
  const [note, setNote] = useState("");
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="resolve-dialog">
        <DialogHeader><DialogTitle>Resolve Line {line.row_number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-zinc-600">Tracking <strong>{line.tracking_number || "—"}</strong> · Variance <strong>{formatCurrency(line.variance)}</strong></p>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Resolution</Label>
            <Select value={resolution} onValueChange={setResolution}>
              <SelectTrigger className="border-zinc-300 rounded-md mt-1" data-testid="resolve-resolution-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="accepted">Accept charge</SelectItem>
                <SelectItem value="disputed">Dispute with courier</SelectItem>
                <SelectItem value="ignored">Ignore</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="border-zinc-300 rounded-md mt-1" data-testid="resolve-note" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="border-zinc-300 rounded-md">Cancel</Button>
            <Button onClick={() => onResolve(line.line_id, resolution, note)} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="resolve-submit">
              Save Resolution
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color = "", big }) {
  return (
    <div className="border border-zinc-200 p-3 bg-zinc-50">
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</p>
      <p className={`font-black tracking-tighter font-mono mt-1 ${big ? "text-2xl" : "text-lg"} ${color}`}>{value}</p>
    </div>
  );
}
